import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";

import { createThumbnailsForPDF } from "@/apis/custom/customRoutes";
import { MainLayout } from "@/components/layouts/main-layout";
import { ErrorCard } from "@/components/shared/errors/error-card";
import SplitPdfLoading from "@/components/shared/pdf-tools/components/split-pdf-loading";
import { EditPdfContainer } from "@/components/shared/pdf-tools/edit-pdf";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";
import { useEditPages } from "@/queries/invoices/edit-pages";
import { useGetInvoice } from "@/queries/invoices/get";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/_authenticated/$company/buchhaltung/_focused/rechnung/$id/seiten-bearbeiten",
)({
  component: PdfSplit,
});

function PdfSplit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: invoiceId } = useParams({ from: Route.id });
  const search = useSearch({ from: Route.id });

  const [pdfs, setPdfs] = useState<
    {
      invoiceId: string;
      fileName: string;
      pdfUrl: string;
      thumbnailUrls?: string[];
    }[]
  >([]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { editPages } = useEditPages();

  const { invoice, loading } = useGetInvoice({
    invoiceId,
  });

  const { getPdfBlob, fetchPdf } = useInvoicePdfCache();

  const { remove: removeInvoiceFromQueue } = useInvoiceBrowser();

  useEffect(() => {
    if (!invoiceId || pdfs.length > 0) return;

    (async () => {
      try {
        const cached = getPdfBlob(invoiceId);
        const pdfUrl = cached ?? (await fetchPdf(invoiceId)).blob;

        setPdfs([
          {
            invoiceId,
            fileName:
              invoice?.invoiceDetails?.invoice?.fileName ?? `${invoiceId}.pdf`,
            pdfUrl,
          },
        ]);
      } catch (error) {
        console.error("Error preparing PDF:", error);
        Sentry.captureException(error);
        setErrorMessage(t("pdf_load_error"));
      }
    })();
  }, [
    invoiceId,
    pdfs.length,
    getPdfBlob,
    fetchPdf,
    t,
    invoice?.invoiceDetails?.invoice?.status,
  ]);

  const handleNavigation = (ids: string[]) => {
    const returnTo = (search as { returnTo?: string }).returnTo;
    if (returnTo) {
      navigate({ to: returnTo });
    } else {
      removeInvoiceFromQueue(ids);
    }
  };

  const handleSubmitLayout = async (
    layout: {
      invoiceId: string;
      pageIndex: number;
    }[][],
  ) => {
    await editPages(layout);
    const invoiceIds = Array.from(
      new Set(layout.flat().map((item) => item.invoiceId)),
    );
    setTimeout(() => {
      handleNavigation(invoiceIds);
    }, 200);
  };

  const isProcessing =
    loading || invoice?.invoiceDetails?.invoice?.status === InvoiceStatus.NEW;

  if (errorMessage) {
    return (
      <div className="flex justify-center items-center flex-1">
        <ErrorCard errorMessage={errorMessage} title="Achtung" />
      </div>
    );
  }

  if (pdfs.length === 0) {
    return (
      <SplitPdfLoading
        onCancel={() => {
          window.history.back();
        }}
      />
    );
  }

  return (
    <EditPdfContainer
      sourceInvoiceId={invoiceId}
      initialPdfBlobs={pdfs}
      onSubmit={handleSubmitLayout}
      isProcessing={isProcessing}
    />
  );
}
